import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import { ApiUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
import { Entity } from '@backstage/catalog-model';
import fetch from 'node-fetch';
import { BadgeSpecification, CombinedBadge } from '../types';

/**
 * Interface defining the methods of the CommonProductService.
 */
export interface ICommonProductService {
  /**
   * Retrieves achievements for a specific product entity.
   * @param product - The product entity for which achievements are retrieved.
   * @returns A promise that resolves to a list of combined badges.
   */
  getAchievements(product: Entity): Promise<CombinedBadge[]>;
}

/**
 * Service class for managing product achievements.
 * @implements ICommonProductService
 *
 * @module CommonProductService
 * @description Provides methods for interacting with product-related achievements in Backstage.
 */
export class CommonProductService implements ICommonProductService {
  private readonly identityApi: IdentityApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly PRODUCT_API_PATH = 'product';

  /**
   * Creates an instance of CommonProductService.
   * @param options - The options to initialize the service.
   * @param options.identityApi - The IdentityApi instance for authentication.
   * @param options.discoveryApi - The DiscoveryApi instance for service discovery.
   */
  constructor(options: {
    identityApi: IdentityApi;
    discoveryApi: DiscoveryApi;
  }) {
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
  }

  /**
   * Authenticates a request using the IdentityApi.
   * @param url - The URL for the request.
   * @param options - Optional request initialization parameters.
   * @returns The authenticated response.
   */
  private async authenticate(
    url: fetch.RequestInfo,
    options?: fetch.RequestInit,
  ) {
    const authorizedRequest = await ApiUtil.authorizeRequest(this.identityApi);
    return await authorizedRequest.execute(url, options);
  }

  /**
   * Retrieves detailed information about a specific achievement by its ID.
   * @param entity - An object containing the namespace and ID of the achievement.
   * @returns The achievement details.
   */
  private async getAchievementById(entity: {
    namespace: string;
    id: string;
  }): Promise<BadgeSpecification> {
    const productApiUrl = await this.discoveryApi.getBaseUrl(
      this.PRODUCT_API_PATH,
    );
    const achievementsById: BadgeSpecification[] = await this.authenticate(
      `${productApiUrl}/${entity.namespace}/badge/${entity.id}`,
    );

    return achievementsById.reduce((acc, achievement) => {
      if (
        new Date(achievement.start_date).getTime() >
        new Date(acc.start_date).getTime()
      ) {
        return achievement;
      }

      return acc;
    }, achievementsById[0]);
  }

  /**
   * Retrieves all achievements for a specific product entity.
   * @param entity - The product entity for which achievements are retrieved.
   * @returns A promise that resolves to a list of achievements with metadata.
   */
  async getAchievements(
    entity: Entity,
  ): ReturnType<ICommonProductService['getAchievements']> {
    let productApiUrl = await this.discoveryApi.getBaseUrl(
      this.PRODUCT_API_PATH,
    );
    productApiUrl = `${productApiUrl}/${entity.metadata.namespace}/${entity.metadata.name}/badge`;

    const achievements = (await this.authenticate(productApiUrl)) as any[];

    const achievementsWithMetadata = () => {
      return achievements.map(async achievement => {
        const achievementInfo = await this.getAchievementById({
          namespace: `${entity.metadata.namespace}/${entity.metadata.name}`,
          id: String(achievement.id),
        });

        return { ...achievement, ...achievementInfo } as CombinedBadge;
      });
    };

    return await Promise.all(achievementsWithMetadata());
  }
}
